import React from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { updateURLState } from '../../lib/urlState';
import { 
  PlusIcon, 
  LightBulbIcon, 
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  UserIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface DashboardProps {
  onPageChange?: (page: string) => void;
  onShowIdeaForm?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onPageChange, onShowIdeaForm }) => {
  const { state } = useApp();

  // Handle clicking on a newest idea to open its details
  const handleIdeaClick = (ideaId: string) => {
    // Set the selected idea ID in URL state
    updateURLState({ selectedIdeaId: ideaId });
    // Navigate to the ideas page
    onPageChange?.('ideas');
  };

  // Calculate metrics
  const totalIdeas = state.ideas.length;
  const ideasByStatus = state.ideas.reduce((acc, idea) => {
    acc[idea.status] = (acc[idea.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const completedTasks = state.tasks.filter(task => task.status === 'Done').length;
  const inProgressTasks = state.tasks.filter(task => task.status === 'In Progress').length;
  const totalTasks = state.tasks.length;

  const avgIdeaScore = state.ideas.reduce((sum, idea) => sum + (idea.score || 0), 0) / state.ideas.length || 0;

  const recentIdeas = state.ideas
    .sort((a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime())
    .slice(0, 5);

  const upcomingTasks = state.tasks
    .filter(task => task.status !== 'Done' && task.dueDate)
    .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
    .slice(0, 5);

  const topTags = state.ideas
    .flatMap(idea => idea.tags)
    .reduce((acc, tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const topTagsArray = Object.entries(topTags)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ElementType;
    iconColor: string;
    trend?: { value: number; positive: boolean };
  }> = ({ title, value, subtitle, icon: Icon, iconColor, trend }) => (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${iconColor}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        {trend && (
          <div className="mt-2 flex items-center">
            <ArrowTrendingUpIcon className={`h-4 w-4 ${trend.positive ? 'text-green-500' : 'text-red-500 rotate-180'}`} />
            <span className={`text-sm font-medium ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
              {trend.positive ? '+' : '-'}{Math.abs(trend.value)}%
            </span>
            <span className="text-sm text-gray-500 ml-1">from last week</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {state.currentUser?.name || 'User'}! 👋
          </h1>
          <p className="text-gray-600 mt-2">
            Kajeet Idea Hopper Main Dashboard
          </p>
        </div>
        <div className="mt-4 lg:mt-0">
          <button 
            onClick={() => onShowIdeaForm?.()}
            className="btn-aqua flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Idea
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Ideas"
          value={totalIdeas}
          subtitle={`${ideasByStatus['New'] || 0} new this week`}
          icon={LightBulbIcon}
          iconColor="bg-electric-lime"
          trend={{ value: 12, positive: true }}
        />
        <MetricCard
          title="Completed Tasks"
          value={`${completedTasks}/${totalTasks}`}
          subtitle={`${Math.round((completedTasks / totalTasks) * 100) || 0}% completion rate`}
          icon={CheckCircleIcon}
          iconColor="bg-green-500"
          trend={{ value: 8, positive: true }}
        />
        <MetricCard
          title="In Progress"
          value={inProgressTasks}
          subtitle="Active tasks"
          icon={ClockIcon}
          iconColor="bg-energetic-blue"
        />
        <MetricCard
          title="Avg. Idea Score"
          value={avgIdeaScore.toFixed(1)}
          subtitle="Value/Effort ratio"
          icon={ArrowTrendingUpIcon}
          iconColor="bg-deep-teal"
          trend={{ value: 5, positive: true }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Newest Ideas */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Newest Ideas</h3>
                <p className="text-xs text-gray-500">Click to view details</p>
              </div>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {recentIdeas.map((idea) => (
                  <div 
                    key={idea.id} 
                    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-md transition-all duration-200 cursor-pointer"
                    onClick={() => handleIdeaClick(idea.id)}
                    title="Click to view idea details"
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-3 h-3 rounded-full ${
                        idea.status === 'New' ? 'bg-blue-400' :
                        idea.status === 'Under Review' ? 'bg-yellow-400' :
                        idea.status === 'In Progress' ? 'bg-purple-400' :
                        idea.status === 'Completed' ? 'bg-green-400' :
                        'bg-gray-400'
                      }`}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {idea.title}
                        </h4>
                        <span className={`badge ml-2 ${
                          idea.category === 'Feature' ? 'badge-primary' :
                          idea.category === 'Product' ? 'badge-success' :
                          idea.category === 'Process' ? 'bg-orange-100 text-orange-800' :
                          idea.category === 'Technology' ? 'bg-purple-100 text-purple-800' :
                          idea.category === 'Sales and Marketing' ? 'bg-pink-100 text-pink-800' :
                          'badge-info'
                        }`}>
                          {idea.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 truncate">
                        {idea.description}
                      </p>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {idea.owner.name}
                        <span className="mx-2">•</span>
                        {format(new Date(idea.dateSubmitted), 'MMM d, yyyy')}
                        <span className="mx-2">•</span>
                        Score: {idea.score?.toFixed(1)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Distribution */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Ideas by Status</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {Object.entries(ideasByStatus).map(([status, count]) => {
                  const percentage = (count / totalIdeas) * 100;
                  return (
                    <div key={status}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{status}</span>
                        <span className="text-sm text-gray-500">{count}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            status === 'New' ? 'bg-blue-500' :
                            status === 'Under Review' ? 'bg-yellow-500' :
                            status === 'In Progress' ? 'bg-purple-500' :
                            status === 'Completed' ? 'bg-emerald-500' :
                            'bg-gray-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Popular Tags */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Popular Tags</h3>
            </div>
            <div className="card-body">
              <div className="flex flex-wrap gap-2">
                {topTagsArray.map(([tag, count]) => (
                  <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-deep-teal bg-opacity-10 text-deep-teal">
                    <TagIcon className="h-3 w-3 mr-1" />
                    {tag} ({count})
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Upcoming Tasks */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {upcomingTasks.length > 0 ? upcomingTasks.map((task) => (
                  <div key={task.id} className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${
                      task.priority === 'High' ? 'bg-red-400' :
                      task.priority === 'Medium' ? 'bg-yellow-400' :
                      'bg-green-400'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        Due {format(new Date(task.dueDate!), 'MMM d')}
                      </p>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm text-gray-500">No upcoming tasks</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 